import { ComponentProps, ComponentType, SVGProps } from 'react';
import classnames from 'classnames';

import { SkeletonSize16, SkeletonSize24, SkeletonSize32 } from '@hh.ru/magritte-ui-icon/Skeleton';
import {
    IconWrapperComponent,
    IconWrapperComponentSize16,
    IconWrapperComponentSize24,
    IconWrapperComponentSize32,
} from '@hh.ru/magritte-ui-icon/iconTypes';

import styles from './icon.less';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
type SizedIconWrapperComponent<T extends string> = T extends `${string}Size16`
    ? IconWrapperComponentSize16
    : T extends `${string}Size24`
      ? IconWrapperComponentSize24
      : T extends `${string}Size32`
        ? IconWrapperComponentSize32
        : IconWrapperComponent;

const INCOMPLETE_SKELETON_BY_SIZE = {
    16: SkeletonSize16,
    24: SkeletonSize24,
    32: SkeletonSize32,
};

type SizedSkeleton<T extends string> = T extends `${string}Size16`
    ? IconWrapperComponentSize16<{ loading?: boolean }>
    : T extends `${string}Size24`
      ? IconWrapperComponentSize24<{ loading?: boolean }>
      : T extends `${string}Size32`
        ? IconWrapperComponentSize32<{ loading?: boolean }>
        : IconWrapperComponent<{ loading?: boolean }>;

type IconWithSkeleton<T extends string> = SizedIconWrapperComponent<T> & { Skeleton: SizedSkeleton<T> };

const hoc = function <T extends string>(Icon: IconComponent, componentName: string): IconWithSkeleton<T> {
    const IconWrapper: IconWrapperComponent = ({
        initial = 'primary',
        'aria-label': ariaLabel,
        'aria-hidden': ariaHidden,
        ...props
    }) => {
        let highlightedValue;
        let disabledValue;
        let propsToPass = { ...props };

        if ('highlighted' in propsToPass) {
            const { highlighted, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            highlightedValue = highlighted;
        }

        if ('disabled' in propsToPass) {
            const { disabled, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            disabledValue = disabled;
        }

        return (
            <Icon
                {...propsToPass}
                aria-label={ariaLabel}
                aria-hidden={ariaLabel ? ariaHidden || undefined : true}
                role="img"
                focusable="false"
                className={classnames(styles.icon, {
                    [styles[`icon_initial-${initial}`]]: !!initial,
                    [styles[`icon_highlighted-${highlightedValue}`]]: !!highlightedValue,
                    [styles[`icon_disabled-${disabledValue}`]]: !!disabledValue,
                })}
            />
        );
    };

    const size = parseInt(componentName.slice(-2), 10) as 16 | 24 | 32;
    const IncompleteSkeleton = INCOMPLETE_SKELETON_BY_SIZE[size];

    const Skeleton = ((props: ComponentProps<typeof IconWrapper> & { loading: boolean }) => (
        <IncompleteSkeleton Icon={IconWrapper} {...props} />
    )) as SizedSkeleton<T>;

    const IconWithSkeleton = IconWrapper as IconWithSkeleton<T>;
    IconWithSkeleton.isIcon = true;
    IconWithSkeleton.Skeleton = Skeleton;
    IconWithSkeleton._size = size;

    return IconWithSkeleton;
};

export default hoc;
